import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import * as pas from '../pas';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useLoginRequest } from './useLoginRequest';
import { WAITING_MESSAGE_LOGIN_TYPE } from './constants';
import { useValidateSession } from './useValidateSession';
import { useAfterLoginAction } from './useAfterLoginAction';
import { useBeforeLoginAction } from './useBeforeLoginAction';
import { useFailedLoginAction } from './useFailedLoginAction';
import { useSuccessLoginAction } from './useSuccessLoginAction';
import { useGetWaitingMessages } from './useGetWaitingMessages';
import { useLoginBySessionToken } from './useLoginBySessionToken';
import { useRedirectToAfterLoginURL } from './useRedirectToAfterLoginURL';
import { useContinueLoginWithSessionToken } from './useContinueLoginWithSessionToken';

export const useLoginBySSO = () => {
    const dispatch = useDispatch();
    const loginBySessionToken = useLoginBySessionToken();
    const loginByCookie = useLoginByCookie();
    const afterLoginAction = useAfterLoginAction();
    const successLoginAction = useSuccessLoginAction();
    const failedLoginAction = useFailedLoginAction();
    const redirectToAfterLoginURL = useRedirectToAfterLoginURL();
    const validateSession = useValidateSession();
    const getWaitingMessages = useGetWaitingMessages();
    const { realMode, languageCode } = useLoginRequest() || {};
    const useDirectPASRequests = useAppSelector(prop('useDirectPASRequests'));
    const continueLoginWithSessionToken = useContinueLoginWithSessionToken();

    return useCallback(async () => {
        dispatch(actions.setIsLoginInProcess(true));
        dispatch(actions.setSsoAttempted());

        try {
            const response = await loginByCookie();
            const { sessionValidationData } = response?.data || {};

            if (sessionValidationData) {
                const userName = helpers.retrieveRememberMeItem();
                const credentials = { userName, password: '', realMode, languageCode, rememberMeLogin: true };

                dispatch(actions.setAutoLoginCompleted());

                const sessionToken = await validateSession(response, { isSSO: true });
                await continueLoginWithSessionToken({ ...credentials, sessionToken });

                return;
            }
        } catch (error) {
            helpers.removeRememberMeItem();

            failedLoginAction({
                data: 'PAS SSO login is failed. Cookie does not exists.',
                ssoLogin: true
            });
            dispatch(actions.setIsLoginInProcess(false));
            dispatch(actions.setAutoLoginCompleted());

            return;
        }

        try {
            const tempTokenResponse = useDirectPASRequests
                ? await pas.getTempTokenDirect()
                : await pas.getTempToken();
            const userName = tempTokenResponse?.data?.username;
            const sessionToken = tempTokenResponse?.data?.sessionToken?.sessionToken;

            await loginBySessionToken(userName, sessionToken);
            await afterLoginAction();
            await successLoginAction({ userName, authType: 'token' });

            getWaitingMessages({ excludeWaitingMessages: WAITING_MESSAGE_LOGIN_TYPE });
            redirectToAfterLoginURL();
        } catch (error) {
            helpers.removeRememberMeItem();
            failedLoginAction({
                data: 'PAS SSO login is failed. Cookie does not exists.',
                ssoLogin: true
            });
        } finally {
            dispatch(actions.setIsLoginInProcess(false));
            dispatch(actions.setAutoLoginCompleted());
        }
    }, [
        realMode,
        languageCode,
        dispatch,
        loginByCookie,
        loginBySessionToken,
        getWaitingMessages,
        afterLoginAction,
        successLoginAction,
        failedLoginAction,
        redirectToAfterLoginURL,
        validateSession,
        useDirectPASRequests,
        continueLoginWithSessionToken
    ]);
};

export const useLoginByCookie = () => {
    const loginByRememberMe = useLoginByRememberMe();

    return useCallback(async () => {
        const response = await pas.getLoggedInPlayer();

        if (response?.data?.cookieExists === '1') {
            return;
        }

        return loginByRememberMe();
    }, [loginByRememberMe]);
};

export const useLoginByRememberMe = () => {
    const { realMode, languageCode } = useLoginRequest() || {};
    const beforeLoginAction = useBeforeLoginAction();

    return useCallback(() => {
        const userName = helpers.retrieveRememberMeItem();
        const credentials = { userName, password: '', realMode, languageCode, rememberMeLogin: true };

        if (!userName) {
            return Promise.reject();
        }

        beforeLoginAction();

        return pas.login(credentials);
    }, [realMode, languageCode, beforeLoginAction]);
};
